'use client';

import { CommunityChannelMessageTextContentType } from '@ps/models/lib/community-channel';

import { MarkdownMessageRenderer } from '../channel-message-view/renderers/MarkdownMessageRenderer';
import { PlainTextMessageRenderer } from '../channel-message-view/renderers/PlainTextMessageRenderer';
import {
  SlateMessageRenderer,
  type SlateNode,
} from '../channel-message-view/renderers/SlateMessageRenderer';

interface Props {
  content: string;
  contentType: CommunityChannelMessageTextContentType;
  className?: string;
}

/**
 * Dispatcher that picks the right renderer for a message's stored
 * content type:
 *
 *  - `slatejs-dom` — parse JSON and walk with `SlateMessageRenderer`.
 *    Falls through to the plain-text renderer if the JSON is
 *    malformed (so we surface *something* rather than a broken
 *    bubble).
 *  - `markdown` — render through `Streamdown`.
 *  - `plain-text` (and unknown) — render with `whitespace-pre-wrap`
 *    + auto-linkified http(s) URLs.
 */
export function MessageContentRenderer({
  content,
  contentType,
  className,
}: Props) {
  if (contentType === CommunityChannelMessageTextContentType.SlateJsDom) {
    const nodes = tryParseSlate(content);
    if (nodes) {
      return <SlateMessageRenderer nodes={nodes} className={className} />;
    }
    return <PlainTextMessageRenderer content={content} className={className} />;
  }
  if (contentType === CommunityChannelMessageTextContentType.Markdown) {
    return <MarkdownMessageRenderer content={content} className={className} />;
  }
  return <PlainTextMessageRenderer content={content} className={className} />;
}

function tryParseSlate(content: string): SlateNode[] | undefined {
  try {
    const parsed = JSON.parse(content);
    if (!Array.isArray(parsed)) return undefined;
    return parsed as SlateNode[];
  } catch {
    return undefined;
  }
}
